import NiceModal from "@ebay/nice-modal-react";
import type * as Scrivito from "scrivito";
import type { NiceModalHandler } from "@ebay/nice-modal-react";
import type { Accessory } from "interfaces/product/accessory/accessory.types";
import type {
  OrderCode,
  NavigateToConfiguratorProps,
  NavigateToProductListProps,
} from "interfaces/product/product.types";
import type {
  FindSeriesKeylistByKeyParams,
  SeriesItem,
} from "./getCategoryOrSeriesMap";
import { ProductConfiguratorModal } from "components/_Modals/ProductConfiguratorModal";
import { getProjectUnit } from "stores/project";
import { toast } from "react-toastify";

type ProductForNavigation = {
  nodes: ProductNavigationNode[];
  serieId: number;
  seriesId: number;
  name: string;
  serie: string;
  key: string;
  isSerie: boolean;
  parentName: string;
  parentCategories: string;
  keyList: string[];
  link: Scrivito.Obj;
};

export interface ModalProps extends NiceModalHandler<Record<string, unknown>> {
  id: string;
  title: string;
  orderCode: OrderCode;
  dummy: string;
  type: string;
  wizardType: string;
  groupPosition: number;
  selectedAccessories: Accessory[];
}

type ProductNavigationNode = {
  countryOfOrigin: string;
  key: string;
  keyList: string[];
  name: string;
  parentId: string;
  pimId: string;
  serie: string;
  serieId: number;
};

type FindCategoryByNameResp = { class: string[]; key: string; name: string };

type ProductNavigationHandlerProps = {
  product: ProductForNavigation;
  isModalActive: boolean;
  id: string;
  type: string;
  wizardType: string;
  modal: ModalProps;
  dummy: string;
  handleBackAction: () => void;
  projectId: string;
  productCategoryId: string[];
  seriesMap: Map<string | number, SeriesItem>;
  navigateToConfiguratorPage: (params: NavigateToConfiguratorProps) => void;
  navigateToProductListPage: (params: NavigateToProductListProps) => void;
  setQuery?: (value: string) => void;
  setCategoryHeadline?: (parentName: string) => void;
  setProductCategoryId: (value: string[]) => void;
  findSeriesKeylistByKey?: (obj: FindSeriesKeylistByKeyParams) => string[];
  findCategoryByName: (name: string) => FindCategoryByNameResp;
  groupPosition: number;
};

export function productNavigationHandler({
  product,
  isModalActive,
  id,
  dummy,
  wizardType,
  handleBackAction,
  modal,
  navigateToConfiguratorPage,
  projectId,
  navigateToProductListPage,
  setProductCategoryId,
  setQuery,
  setCategoryHeadline,
  findSeriesKeylistByKey,
  seriesMap,
  productCategoryId,
  findCategoryByName,
  groupPosition,
}: ProductNavigationHandlerProps) {
  const {
    link,
    nodes,
    serieId,
    seriesId,
    serie,
    key,
    parentName,
    parentCategories,
  } = product;

  if (link) {
    const url = (link.get("linkForMenuItem") as Scrivito.Link).url();
    window.open(url ?? undefined, "_blank");
    return;
  }

  if (!nodes) {
    if (isModalActive) {
      const unit = getProjectUnit();
      if (!unit) {
        toast.error(
          "Unit is not defined. Please select a unit in the project settings.",
        );
        return;
      }
      NiceModal.show(ProductConfiguratorModal, {
        wizardArgs: {
          seriesId: seriesId || serieId,
          groupPosition,
          wizardType,
          dummyType: dummy,
          nodeId: id,
        },
        forceLoadDefaultWizardProduct: true,
        cleanupOnClose: true,
        handleBack: handleBackAction,
      });
      if (modal && typeof modal.remove === "function") {
        modal.remove();
      }
    } else {
      if (setQuery) {
        setQuery("");
      }
      if (setCategoryHeadline) {
        setCategoryHeadline(parentName);
      }

      const selectedSerieId = serieId;
      if (findSeriesKeylistByKey) {
        setProductCategoryId(
          findSeriesKeylistByKey({ seriesMap, targetKey: key }),
        );
      } else {
        setProductCategoryId(product.keyList);
      }

      if ((selectedSerieId && product.isSerie) || (selectedSerieId && serie)) {
        navigateToConfiguratorPage({
          seriesId: selectedSerieId.toString(),
          selectedCategory: productCategoryId,
          projectId,
          shouldKeepProductCategories: true,
        });
      } else {
        if (findCategoryByName) {
          setProductCategoryId(findCategoryByName(parentCategories)?.class);
        }
        navigateToProductListPage({
          projectId,
          shouldKeepProductCategories: true,
        });
      }
    }
  } else {
    if (setProductCategoryId && product.keyList) {
      setProductCategoryId(product.keyList);
    }
    navigateToProductListPage({
      projectId,
      shouldKeepProductCategories: true,
    });
  }
}
